<script setup>
import { Comment, ref, onMounted, watch, useSlots } from 'vue'

const emit = defineEmits(['input:updateModel', 'selectSegment'])

defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  // Mantém os segmentos montados ao trocar de aba (evita remount/refetch).
  keepAlive: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ type: Number })

const selectedIndex = ref(0)
const slots = useSlots()
const segments = ref([])

const selectSegment = i => {
  selectedIndex.value = i
  emit('selectSegment', i)
}

onMounted(() => {
  // Filtra Segments removidos por v-if (que viram vnodes Comment) para não criar tabs fantasmas.
  segments.value = slots
    .default()
    .filter(vnode => vnode.type !== Comment)
    .map((vnode, index) => ({
      title: vnode.props?.title || `Segment ${index + 1}`,
      icon: vnode.props?.icon || '',
      content: vnode,
    }))

  if (model.value) {
    selectedIndex.value = model.value
  }
})

watch(selectedIndex, newVal => {
  emit('update:modelValue', newVal)
})

watch(
  () => model.value,
  newVal => {
    selectSegment(newVal)
  },
)
</script>

<template>
  <div data-segmented-control class="flex flex-col gap-3">
    <div
      data-segmented-control-tabs
      role="tablist"
      class="border-neutral-40 bg-neutral-10 text-neutral-60 inline-flex w-fit flex-row items-center rounded-lg border-[0.5px] p-1"
      :class="{
        'p-[2px] text-sm': size === 'sm',
        'p-[6px] text-sm': size === 'md',
        'p-[4px] text-base': size === 'lg',
      }"
    >
      <button
        v-for="(item, index) in segments"
        :key="index"
        type="button"
        data-segmented-control-tab
        role="tab"
        :aria-selected="selectedIndex === index"
        @click="selectSegment(index)"
        :class="[
          {
            'border-neutral-40 text-primary-90 rounded-lg border-[0.5px] bg-white shadow-md':
              selectedIndex === index,
            'px-2 py-1': size === 'sm',
            'px-4 py-2': size === 'md',
            'px-4 py-2.5': size === 'lg',
          },
        ]"
        class="flex cursor-pointer items-center px-4 py-[10px] leading-normal font-semibold whitespace-nowrap"
      >
        <ImoIcon v-if="item.icon" :name="item.icon" :size="20" class="mr-2" />
        {{ item.title }}
      </button>
    </div>
    <div
      data-segmented-control-content
      role="tabpanel"
      class="border-neutral-20 relative flex w-full flex-col rounded-xl border p-4 break-words"
    >
      <!-- keepAlive: monta todos os segmentos uma vez e alterna via v-show (sem remount/refetch). -->
      <template v-if="keepAlive">
        <div v-for="(segment, index) in segments" v-show="index === selectedIndex" :key="index">
          <component :is="segment.content" />
        </div>
      </template>
      <component v-else :is="segments[selectedIndex]?.content" />
    </div>
  </div>
</template>
